import { httpClient } from '../api/http.client';

// ============================================================================
// Settings
// ============================================================================

export interface AvitoSettings {
  id: number;
  storeName: string;
  storeAddress: string;
  idPrefix: string;
  idFormat: string;
  xmlToken: string;
  xmlUrl?: string;
  autoUpdateEnabled: boolean;
  lastXmlGeneration?: string;
  lastxmlGeneration?: string; // API может возвращать с маленькой буквы
  createdAt: string;
  updatedAt: string;
}

export interface UpdateAvitoSettingsRequest {
  storeName: string;
  storeAddress: string;
  idPrefix: string;
  idFormat: string;
  autoUpdateEnabled?: boolean;
}

// ============================================================================
// Templates
// ============================================================================

export interface AvitoTemplate {
  id: number;
  categoryKey: string;
  titleTemplate: string;
  descriptionTemplate: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAvitoTemplateRequest {
  categoryKey: string;
  titleTemplate: string;
  descriptionTemplate: string;
}

export interface UpdateAvitoTemplateRequest {
  titleTemplate: string;
  descriptionTemplate: string;
}

// ============================================================================
// Category Mappings
// ============================================================================

export interface AvitoСategoryMapping {
  id: number;
  categoryId: number;
  subcategoryId: number | null;
  avitoGender: string;
  avitoCategorySlug: string;
  avitoApparelType: string | null;    // Для одежды: "Кофты и футболки"
  avitoGoodsSubType: string | null;   // Для одежды: "Футболка", "Худи"
  createdAt: string;
  updatedAt: string;
}

export interface CreateAvitoMappingRequest {
  categoryId: number;
  subcategoryId?: number;
  avitoGender: string;
  avitoCategorySlug: string;
  avitoApparelType?: string;          // Для одежды
  avitoGoodsSubType?: string;         // Для одежды
}

export interface UpdateAvitoMappingRequest {
  avitoGender: string;
  avitoCategorySlug: string;
  avitoApparelType?: string;          // Для одежды
  avitoGoodsSubType?: string;         // Для одежды
}

// ============================================================================
// Categories
// ============================================================================

export interface AvitoCategory {
  id: string;
  name: string;
  avitoCategory: string;
  avitoGoodsType: string;
  avitoApparelType: string;
  requiredFields: string[];
  sizeRange: string;
  example: {
    gender: string;
    categorySlug: string;
  };
}

// ============================================================================
// XML Generation
// ============================================================================

export interface GenerateXMLResponse {
  message: string;
  xml?: string;
}

// ============================================================================
// Health Check
// ============================================================================

export interface AvitoHealthCheck {
  status: string;
  timestamp: string;
}

// ============================================================================
// Legacy (старый API)
// ============================================================================

export interface AvitoStatistics {
  totalAds: number;
  activeAds: number;
  views: number;
  contacts: number;
  favorites: number;
  period: {
    start: string;
    end: string;
  };
}

export interface AvitoAd {
  id: string;
  title: string;
  price: number;
  status: 'active' | 'inactive' | 'pending';
  views: number;
  contacts: number;
  createdAt: string;
}

// ============================================================================
// Repository
// ============================================================================

export class AvitoRepository {
  // Settings
  async getSettings(): Promise<AvitoSettings> {
    return httpClient.get<AvitoSettings>('/api/admin/avito/settings');
  }

  async updateSettings(data: UpdateAvitoSettingsRequest): Promise<AvitoSettings> {
    return httpClient.put<AvitoSettings>('/api/admin/avito/settings', data);
  }

  async regenerateXMLToken(): Promise<{ token: string; url: string }> {
    return httpClient.post<{ token: string; url: string }>('/api/admin/avito/xml/regenerate-token', {});
  }

  // Templates
  async getAllTemplates(): Promise<AvitoTemplate[]> {
    const response = await httpClient.get<{ templates: AvitoTemplate[]; total: number }>('/api/admin/avito/templates');
    return response.templates || [];
  }

  async getTemplate(key: string): Promise<AvitoTemplate> {
    return httpClient.get<AvitoTemplate>(`/api/admin/avito/templates/${key}`);
  }

  async createTemplate(data: CreateAvitoTemplateRequest): Promise<AvitoTemplate> {
    return httpClient.post<AvitoTemplate>('/api/admin/avito/templates', data);
  }

  async updateTemplate(key: string, data: UpdateAvitoTemplateRequest): Promise<AvitoTemplate> {
    return httpClient.put<AvitoTemplate>(`/api/admin/avito/templates/${key}`, data);
  }

  async deleteTemplate(key: string): Promise<void> {
    return httpClient.delete(`/api/admin/avito/templates/${key}`);
  }

  // Category Mappings
  async getAllMappings(): Promise<AvitoСategoryMapping[]> {
    const response = await httpClient.get<{ mappings: AvitoСategoryMapping[]; total: number }>('/api/admin/avito/mappings');
    return response.mappings || [];
  }

  async getMapping(id: number): Promise<AvitoСategoryMapping> {
    return httpClient.get<AvitoСategoryMapping>(`/api/admin/avito/mappings/${id}`);
  }

  async createMapping(data: CreateAvitoMappingRequest): Promise<AvitoСategoryMapping> {
    return httpClient.post<AvitoСategoryMapping>('/api/admin/avito/mappings', data);
  }

  async updateMapping(id: number, data: UpdateAvitoMappingRequest): Promise<AvitoСategoryMapping> {
    return httpClient.put<AvitoСategoryMapping>(`/api/admin/avito/mappings/${id}`, data);
  }

  async deleteMapping(id: number): Promise<void> {
    return httpClient.delete(`/api/admin/avito/mappings/${id}`);
  }

  // Categories
  async getSupportedCategories(): Promise<AvitoCategory[]> {
    const response = await httpClient.get<{ categories: AvitoCategory[]; total: number }>('/api/admin/avito/categories');
    return response.categories || [];
  }

  // XML Generation
  async generateXML(): Promise<GenerateXMLResponse> {
    return httpClient.post<GenerateXMLResponse>('/api/admin/avito/generate-xml', {});
  }

  // Health Check
  async healthCheck(): Promise<AvitoHealthCheck> {
    return httpClient.get<AvitoHealthCheck>('/api/admin/avito/health');
  }

  // Legacy API (старый)
  async getStatistics(): Promise<AvitoStatistics> {
    return httpClient.get<AvitoStatistics>('/api/admin/integrations/avito/statistics');
  }

  async getAds(): Promise<{ ads: AvitoAd[]; total: number }> {
    return httpClient.get<{ ads: AvitoAd[]; total: number }>('/api/admin/integrations/avito/ads');
  }

  async getStatus(): Promise<{ connected: boolean; lastSync?: string }> {
    return httpClient.get<{ connected: boolean; lastSync?: string }>('/api/admin/integrations/avito/status');
  }
}

export const avitoRepository = new AvitoRepository();

